import { createTheme } from '@mui/material/styles';

export type PageLayout =
  | 'sidebar-list'
  | 'split-panel'
  | 'single-focus'
  | 'dashboard-grid'
  | 'bottom-sheet'
  | 'chat-flow'

// Brand palette — navy + teal aligned with AirScribe logo
export const glass = {
  bg: 'linear-gradient(145deg, #d6e8f4 0%, #e2eef8 35%, #eef5fb 70%, #f6f9fd 100%)',
  card: 'rgba(255,255,255,0.6)',
  cardBorder: 'rgba(255,255,255,0.7)',
  blur: 'blur(20px)',
  green: '#c5e8b0',
  greenDark: '#a8e6a0',
  yellow: '#f5e680',
  yellowDark: '#f0d860',
  purple: '#d8c8f0',
  purpleDark: '#c4b0e8',
  coral: '#f0b0a0',
  coralDark: '#e89080',
  text: '#1a2335',
  textSecondary: '#5a6a80',
};

// Extend the Theme interface to include accent colors
declare module '@mui/material/styles' {
  interface Palette {
    accent: {
      green: string;
      greenDark: string;
      yellow: string;
      yellowDark: string;
      purple: string;
      purpleDark: string;
      coral: string;
      coralDark: string;
    };
  }
  interface PaletteOptions {
    accent?: {
      green?: string;
      greenDark?: string;
      yellow?: string;
      yellowDark?: string;
      purple?: string;
      purpleDark?: string;
      coral?: string;
      coralDark?: string;
    };
  }
}

export const theme = createTheme({
  palette: {
    primary: { main: '#1e3a5f' },
    secondary: { main: '#3bb8b8' },
    background: { default: '#eef4fb', paper: glass.card },
    text: { primary: glass.text, secondary: glass.textSecondary },
    accent: {
      green: glass.green,
      greenDark: glass.greenDark,
      yellow: glass.yellow,
      yellowDark: glass.yellowDark,
      purple: glass.purple,
      purpleDark: glass.purpleDark,
      coral: glass.coral,
      coralDark: glass.coralDark,
    },
  },
  shape: { borderRadius: 20 },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 16 },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          background: glass.card,
          backdropFilter: glass.blur,
          WebkitBackdropFilter: glass.blur,
          border: `1px solid ${glass.cardBorder}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          background: glass.card,
          backdropFilter: glass.blur,
          WebkitBackdropFilter: glass.blur,
          border: `1px solid ${glass.cardBorder}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.55)',
          backdropFilter: glass.blur,
          WebkitBackdropFilter: glass.blur,
          boxShadow: '0 -2px 20px rgba(0,0,0,0.06)',
          color: glass.text,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            background: 'rgba(255,255,255,0.5)',
            backdropFilter: 'blur(10px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 50, fontWeight: 600 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          background: 'rgba(255,255,255,0.82)',
          backdropFilter: glass.blur,
          WebkitBackdropFilter: glass.blur,
          border: `1px solid ${glass.cardBorder}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 14, marginBottom: 4 },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        },
      },
    },
  },
});

// Keep PageLayout type exported, default layout
export const defaultLayout: PageLayout = 'bottom-sheet';
